import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import UsageMeter from './UsageMeter';
import './Header.css';

// Pretty title for the active route — shown in the center of the
// mobile topbar where the tabs used to live (they're in the sidebar).
function pageTitleFor(pathname) {
  if (pathname === '/image-generator') return 'Images';
  if (pathname === '/assistants') return 'Characters';
  return 'Chat';
}

function Header({ onToggleMobileSidebar, isMobile, onSubscribe, onRequestLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme, fontSize, setFontSize } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [tabMenuOpen, setTabMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const tabMenuRef = useRef(null);

  const isImages = location.pathname === '/image-generator';
  const isAssist = location.pathname === '/assistants';
  const isChat = !isImages && !isAssist;

  // Close both popovers when clicking outside or hitting Esc. One
  // shared listener — cheap and avoids two competing effects.
  useEffect(() => {
    if (!menuOpen && !tabMenuOpen) return undefined;
    const onDown = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (tabMenuOpen && tabMenuRef.current && !tabMenuRef.current.contains(e.target)) {
        setTabMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setMenuOpen(false); setTabMenuOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, tabMenuOpen]);

  // Auto-close both popovers on route changes — picking a tab from
  // the dropdown navigates, which should also close the dropdown.
  useEffect(() => { setMenuOpen(false); setTabMenuOpen(false); }, [location.pathname]);

  return (
    <div className="topbar">
      {isMobile ? (
        <button className="mob-menu-btn" onClick={onToggleMobileSidebar} aria-label="Menu">
          <Menu size={18} />
        </button>
      ) : (
        <div style={{ width: 80 }} />
      )}

      {/* Desktop: tabs in the center. Mobile: page title is a button
          that drops down the three tabs — the cleanest way to keep
          tab switching one tap away without crowding the topbar. */}
      {isMobile ? (
        <div ref={tabMenuRef} className="topbar-tabmenu">
          <button
            className="topbar-tabmenu-btn"
            onClick={() => setTabMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={tabMenuOpen}
          >
            {pageTitleFor(location.pathname)}
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round"
              className={`topbar-tabmenu-chev ${tabMenuOpen ? 'open' : ''}`}
              aria-hidden
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {tabMenuOpen && (
            <div className="topbar-tabmenu-pop" role="menu">
              <button
                className={`topbar-tabmenu-item ${isChat ? 'on' : ''}`}
                onClick={() => { navigate('/'); setTabMenuOpen(false); }}
                role="menuitem"
              >
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
                Chat
              </button>
              <button
                className={`topbar-tabmenu-item ${isImages ? 'on' : ''}`}
                onClick={() => { navigate('/image-generator'); setTabMenuOpen(false); }}
                role="menuitem"
              >
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                Images
              </button>
              <button
                className={`topbar-tabmenu-item ${isAssist ? 'on' : ''}`}
                onClick={() => { navigate('/assistants'); setTabMenuOpen(false); }}
                role="menuitem"
              >
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                </svg>
                Characters
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="nav-tabs">
          <button
            className={`ntab ${isChat ? 'on' : ''}`}
            onClick={() => navigate('/')}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            Chat
          </button>
          <button
            className={`ntab ${isImages ? 'on' : ''}`}
            onClick={() => navigate('/image-generator')}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
            Images
          </button>
          <button
            className={`ntab ${isAssist ? 'on' : ''}`}
            onClick={() => navigate('/assistants')}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
            </svg>
            Characters
          </button>
        </div>
      )}

      <div className="top-right">
        {/* Token/image usage chip — sits to the left of the kebab on
            mobile so it's always glanceable. On desktop it lives next
            to the theme/font controls. Hidden on the Characters page
            to keep the header focused — quota still lives in Settings. */}
        {!isAssist && (
          <UsageMeter onSubscribe={onSubscribe} onRequestLogin={onRequestLogin} compact={isMobile} />
        )}
        {isMobile ? (
          // ── Mobile: single overflow menu (⋮) ──
          // Theme + font controls live inside this dropdown so the
          // topbar stays roomy enough to render the page title without
          // truncation. Tabs were moved to the sidebar long ago.
          <div ref={menuRef} className="topbar-overflow">
            <button
              className="topbar-overflow-btn"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="5" r="1.7"/>
                <circle cx="12" cy="12" r="1.7"/>
                <circle cx="12" cy="19" r="1.7"/>
              </svg>
            </button>
            {menuOpen && (
              <div className="topbar-overflow-menu" role="menu">
                <div className="topbar-overflow-label">Theme</div>
                <div className="topbar-overflow-pair">
                  <button
                    className={`th-btn ${theme === 'light' ? 'on' : ''}`}
                    onClick={() => { setTheme('light'); setMenuOpen(false); }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <circle cx="12" cy="12" r="4"/>
                      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
                    </svg>
                    Light
                  </button>
                  <button
                    className={`th-btn ${theme === 'dark' ? 'on' : ''}`}
                    onClick={() => { setTheme('dark'); setMenuOpen(false); }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                    </svg>
                    Dark
                  </button>
                </div>

                <div className="topbar-overflow-divider" />

                <div className="topbar-overflow-label">Text size</div>
                <div className="topbar-overflow-fs">
                  <button className="fs-btn" onClick={() => setFontSize(fontSize - 1)} aria-label="Decrease text size">−</button>
                  <span className="fs-val">{fontSize}px</span>
                  <button className="fs-btn" onClick={() => setFontSize(fontSize + 1)} aria-label="Increase text size">+</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          // ── Desktop: inline controls (no overflow menu) ──
          <>
            <div className="fs-row">
              <button className="fs-btn" onClick={() => setFontSize(fontSize - 1)} aria-label="Decrease font size">−</button>
              <span className="fs-val">{fontSize}</span>
              <button className="fs-btn" onClick={() => setFontSize(fontSize + 1)} aria-label="Increase font size">+</button>
            </div>
            <div className="theme-row">
              <button
                className={`th-btn ${theme === 'light' ? 'on' : ''}`}
                onClick={() => setTheme('light')}
              >Light</button>
              <button
                className={`th-btn ${theme === 'dark' ? 'on' : ''}`}
                onClick={() => setTheme('dark')}
              >Dark</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Header;
